import { Box3, Raycaster, Vector3 } from 'three';

import { SkyviewCamera } from '../../cameras/types';
import { CoordinateManager } from '../../coordinate-manager';
import { Tiled3dModel } from './type';

export class Tiled3DModelHandle extends Tiled3dModel {
  static get instance() {
    return this._instance;
  }

  getType():
    | '3d-tiles-renderer'
    | 'skymap-tiles-renderer'
    | 'gltf-tiles-renderer'
    | '3tz-tiles-renderer' {
    return this.activeTiledModel?.getType() ?? 'skymap-tiles-renderer';
  }

  private static _instance: Tiled3DModelHandle = new Tiled3DModelHandle();

  private activeTiledModel?: Tiled3dModel;

  private constructor() {
    super();
  }

  async activate(): Promise<void> {
    await this.activeTiledModel?.activate();
  }

  setActiveTiledModel(activeTiledModel: Tiled3dModel | undefined) {
    this.activeTiledModel = activeTiledModel;
  }

  getActiveTiledModel() {
    return this.activeTiledModel;
  }

  getOrigo(): Vector3 {
    return this.activeTiledModel?.getOrigo() ?? new Vector3();
  }

  isLoading() {
    return this.activeTiledModel?.isLoading() ?? false;
  }

  loadLodChunks(camera: SkyviewCamera): Promise<void> {
    if (!this.activeTiledModel) {
      return Promise.resolve();
    }

    this.placeAtOrigo();
    return this.activeTiledModel.loadLodChunks(camera);
  }

  cullLodChunks(camera: SkyviewCamera): void {
    if (!this.activeTiledModel) {
      return;
    }

    this.placeAtOrigo();
    this.activeTiledModel.cullLodChunks(camera);
  }

  pick(ray: Raycaster) {
    this.placeAtOrigo();
    return this.activeTiledModel?.pick(ray) ?? [];
  }

  intersect(rayCaster: Raycaster) {
    this.placeAtOrigo();
    return this.activeTiledModel?.intersect(rayCaster) ?? [];
  }

  pickNearCursor(camera: SkyviewCamera, rayCaster: Raycaster) {
    this.placeAtOrigo();
    return this.activeTiledModel?.pickNearCursor(camera, rayCaster);
  }

  getBoundingBox(): Box3 {
    this.placeAtOrigo();
    return this.activeTiledModel?.getBoundingBox() ?? new Box3();
  }

  /**
   * Places the model, which is drawn around its own origo, where that is in the scene, as the point
   * clouds are, so the scene's origo can be anywhere.
   */
  private placeAtOrigo() {
    const model = this.activeTiledModel;
    const projectionOrigo = CoordinateManager.getInstance().getProjectionOrigo();
    if (!model || !projectionOrigo) {
      return;
    }

    model.position.copy(model.getOrigo()).sub(projectionOrigo);
    model.updateWorldMatrix(false, true);
  }

  clearCustom() {
    this.activeTiledModel?.clear();
    this.activeTiledModel?.clearCustom();
  }
}
